import React, { useEffect, useRef, useState } from "react";
import { Editable, EditableTextarea, EditablePreview, useEditableControls, Button, } from "@chakra-ui/react";
import TextareaAutosize from "react-textarea-autosize";
import { Row } from "./row";
import { Box } from "./box";
export function EditableDescription({ isDisabled = false, externalValue, externalValueSource, value, width = "lg", placeholder = "Add a description…", onCancel, onChange, onSubmit, ...props }) {
    const [isEditing, setIsEditing] = useState(false);
    const [isTruncated, setIsTruncated] = useState(false);
    const previewElementRef = useRef(null);
    useEffect(() => {
        if (isEditing) {
            return;
        }
        const truncatedTextHeight = previewElementRef.current?.offsetHeight ?? 0;
        const fullTextHeight = previewElementRef.current?.scrollHeight ?? 0;
        setIsTruncated(truncatedTextHeight < fullTextHeight);
    }, [isEditing, value]);
    const [isExpanded, setIsExpanded] = useState(false);
    const trimmedExternalValue = externalValue?.trim();
    let trimmedValue = value.trim();
    let textColor = "text.primary";
    if (trimmedValue.length === 0) {
        textColor = "gray.500";
        if (trimmedExternalValue && externalValueSource) {
            trimmedValue = `${trimmedExternalValue} (sourced from ${externalValueSource})`;
            textColor = "text.secondary";
        }
    }
    return (React.createElement(Editable, { isPreviewFocusable: true, isDisabled: isDisabled, placeholder: placeholder, selectAllOnFocus: false, value: isEditing ? value : trimmedValue, width: width, ...props, onEdit: () => {
            setIsEditing(true);
            setIsExpanded(false);
        }, onChange: onChange, onCancel: (value) => {
            setIsEditing(false);
            onCancel?.(value);
        }, onSubmit: (value) => {
            setIsEditing(false);
            onSubmit(value);
        } },
        React.createElement(Box, { display: "inline-block", position: "relative", role: "group" },
            React.createElement(Box, { position: "absolute", top: 0, right: -2, bottom: 0, left: -2, bg: "rgba(0, 0, 0, 0.05)", borderRadius: "md", pointerEvents: "none", transition: "all 100ms ease-in-out", opacity: 0, transform: "scale(0.9)", _groupHover: {
                    opacity: 1,
                    transform: "scale(1)",
                } }),
            React.createElement(EditablePreview, { ref: previewElementRef, as: "p", lineHeight: "5", fontSize: "md", color: textColor, cursor: trimmedValue.length === 0 ? "pointer" : undefined, noOfLines: isExpanded ? undefined : 2, whiteSpace: "pre-wrap", maxWidth: width })),
        React.createElement(EditableTextarea, { as: TextareaAutosize, lineHeight: "5", fontSize: "md", resize: "none", minRows: 2, maxRows: 10, scrollPaddingBottom: 1, _focusVisible: {
                boxShadow: "outline",
            } }),
        isEditing && (React.createElement(EditableControls, { externalValue: trimmedExternalValue, externalValueSource: externalValueSource, onChange: onChange })),
        isTruncated && !isEditing && (React.createElement(Box, null,
            React.createElement(Button, { color: "#0E6B93", variant: "link", onClick: () => {
                    setIsExpanded((previousValue) => !previousValue);
                } }, isExpanded ? "See less…" : "See more…")))));
}
// eslint-disable-next-line @typescript-eslint/no-empty-function
const noop = () => { };
function EditableControls({ externalValue, externalValueSource, onChange, }) {
    const { getSubmitButtonProps, getCancelButtonProps } = useEditableControls();
    return (React.createElement(Row, { mt: 1, gap: 4 },
        React.createElement(Button, { color: "#0E6B93", variant: "link", onClick: noop, ...getSubmitButtonProps() }, "Save"),
        React.createElement(Button, { color: "#0E6B93", variant: "link", onClick: noop, ...getCancelButtonProps() }, "Cancel"),
        externalValue && (React.createElement(Button, { ml: "auto", color: "#0E6B93", variant: "link", onPointerDown: (event) => {
                // `Editable` component only recognizes submit (`getSubmitButtonProps`) and
                // cancel (`getCancelButtonProps`) buttons that interact with `Editable` and
                // aren't seen as if user clicked away and stopped editing.
                //
                // To avoid `Editable` from stopping edit, we catch `pointerdown` event before
                // `Editable` does and silence it.
                event.preventDefault();
            }, onClick: () => {
                onChange(externalValue);
            } },
            "Use ",
            externalValueSource,
            " description"))));
}
